import { useState, useEffect, useMemo } from 'react';

import { useSupabase } from '../context/SupabaseContext';
import { useConfig } from '../context/ConfigContext';

import type { LibraryCollection, LibraryItem, MediaRef, TMDBItem } from '../types';
import { TMDB_API_BASE_URL, LANGUAGE } from '../constants';

const detailsCache: Record<string, LibraryItem> = {};

export const useLibrary = (profileId: number | null | undefined) => {
  const { supabase } = useSupabase();
  const { tmdbApiKey } = useConfig();

  const [library, setLibrary] = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(false);

  const collections = useMemo(() => ({
    towatch: library.filter(i => i.collection === 'towatch'),
    watched: library.filter(i => i.collection === 'watched'),
    rewatch: library.filter(i => i.collection === 'rewatch'),
    ignored: library.filter(i => i.collection === 'ignored'),
  }), [library]);

  // Helper to transform raw TMDB API data into MediaPreview format
  const normalizeMetadata = (raw: TMDBItem) => ({
    displayTitle: raw.media_type === 'movie' ? raw.title : raw.name,
    displayDate: raw.media_type === 'movie' ? raw.release_date : raw.first_air_date,
    poster_path: raw.poster_path,
    vote_average: raw.vote_average,
  });

  // Effect to fetch user library on supabase, profileId or tmdbApiKey change
  useEffect(() => {
    if (!supabase || !profileId || !tmdbApiKey) return;

    const fetchFullLibrary = async () => {
      setLoading(true);

      const { data: dbItems, error } = await supabase
        .from('user_library')
        .select('*')
        .eq('profile_id', profileId);

      if (error || !dbItems) {
        setLoading(false);
        return;
      }

      const hydratedResults = await Promise.all(
        dbItems.map(async (dbRow: any) => {
          // Map DB columns to the LibraryItem interface
          const item: LibraryItem = {
            id: dbRow.media_id,
            media_type: dbRow.media_type,
            profile_id: dbRow.profile_id,
            collection: dbRow.collection,
            displayTitle: dbRow.displayTitle,
            displayDate: dbRow.displayDate,
            poster_path: dbRow.poster_path,
            vote_average: dbRow.vote_average,
          };

          // If metadata is already in DB, we are done
          if (item.displayTitle) {
            return item;
          }

          // Legacy Repair: Fetch from TMDB if DB metadata is missing
          try {
            const res = await fetch(
              `${TMDB_API_BASE_URL}/${item.media_type}/${item.id}?api_key=${tmdbApiKey}&language=${LANGUAGE}`
            );
            const rawData: TMDBItem = await res.json();
            rawData.media_type = item.media_type; // Ensure type is present for normalization

            const normalized = normalizeMetadata(rawData);
            const hydratedItem = { ...item, ...normalized };

            upsertToDb(hydratedItem, item.collection);
            return hydratedItem;
          } catch (err) {
            console.error("Hydration error", err);
            return item;
          }
        }));

      setLibrary(hydratedResults);
      setLoading(false);
    };

    fetchFullLibrary();
  }, [supabase, profileId, tmdbApiKey]);

  // --- HELPERS FOR DB MUTATION ---

  const removeFromDb = async (itemId: number, type: string) => {
    if (!supabase || !profileId) return;
    await supabase
      .from('user_library')
      .delete()
      .match({ profile_id: profileId, media_id: itemId, media_type: type });
  };

  const upsertToDb = async (item: LibraryItem | (MediaRef & Partial<LibraryItem>), collection: LibraryCollection) => {
    if (!supabase || !profileId) return;

    // Only send the metadata if it exists
    const payload: any = {
      profile_id: profileId,
      media_id: item.id,
      media_type: item.media_type,
      collection: collection,
    };

    if (item.displayTitle) payload.displayTitle = item.displayTitle;
    if (item.poster_path) payload.poster_path = item.poster_path;
    if (item.vote_average) payload.vote_average = item.vote_average;
    if (item.displayDate) payload.displayDate = item.displayDate;

    const { error } = await supabase
      .from('user_library')
      .upsert(payload, { onConflict: 'profile_id,media_id,media_type' });

    if (error) console.error("Supabase Error:", error.message);
  };

  // --- TOGGLE LOGIC ---

  const updateCollection = async (ref: MediaRef, target: LibraryCollection) => {
    if (!profileId) return;

    const previousLibrary = [...library];
    const existing = library.find(m => m.id === ref.id && m.media_type === ref.media_type);

    // REMOVE: User clicked the collection the item is already in
    if (target === null || existing?.collection === target) {
      setLibrary(prev => prev.filter(m => !(m.id === ref.id && m.media_type === ref.media_type)));
      await removeFromDb(ref.id, ref.media_type);
      return;
    }

    const optimisticItem: LibraryItem = existing
      ? { ...existing, collection: target }
      : {
        ...ref,
        profile_id: profileId,
        collection: target,
        displayTitle: '', // Skeleton state
        poster_path: '',
        vote_average: 0,
        displayDate: ''
      };

    // MOVE or ADD
    setLibrary(prev => {
      const filtered = prev.filter(m => !(m.id === ref.id && m.media_type === ref.media_type));
      return [...filtered, optimisticItem];
    });

    // 3. BACKGROUND HYDRATION & DB PERSISTENCE
    try {
      let finalData = optimisticItem;

      // Only fetch if we don't have metadata yet
      if (!existing?.displayTitle) {
        const cacheKey = `${ref.media_type}-${ref.id}`;

        if (detailsCache[cacheKey]) {
          finalData = { ...detailsCache[cacheKey], collection: target };
        } else {
          const res = await fetch(
            `${TMDB_API_BASE_URL}/${ref.media_type}/${ref.id}?api_key=${tmdbApiKey}&language=${LANGUAGE}`
          );
          const raw: TMDBItem = await res.json();
          raw.media_type = ref.media_type;

          finalData = {
            ...ref,
            profile_id: profileId,
            collection: target,
            ...normalizeMetadata(raw),
          };
          detailsCache[cacheKey] = finalData;
        }

        // Update local state again with full metadata once fetched
        setLibrary(prev => prev.map(m =>
          (m.id === ref.id && m.media_type === ref.media_type) ? finalData : m
        ));
      }

      // Persist to Supabase
      await upsertToDb(finalData, target);

    } catch (err) {
      console.error("Library update failed:", err);
      setLibrary(previousLibrary);
    }
  };

  return {
    ...collections,
    library,
    updateCollection,
    loading
  };
};